/**
 * DEPENDENCY CHECK DIALOG
 *
 * Modal dialog that displays the status of required dependencies:
 * Python, Git, Claude CLI, and Anthropic API Key.
 * Allows entering an API key and re-running the check.
 * When isBlocking=true, the dialog cannot be dismissed until all deps are ok.
 */

import { useState } from "react";
import { CheckCircle2, XCircle, AlertTriangle, RefreshCw, Loader2, Key } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useWorkerStore } from "@/stores/workerStore";
import { isTauri } from "@/hooks/usePlatform";
import type { DepCheck } from "@/types/worker";

interface DepsCheckDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isBlocking?: boolean;
}

const DEP_LABELS: { key: string; label: string }[] = [
  { key: "python", label: "Python 3.10+" },
  { key: "git", label: "Git" },
  { key: "claude", label: "Claude CLI" },
  { key: "api_key", label: "Anthropic API Key (needed for API fallback when CLI times out)" },
];

function DepRow({ label, dep, isOptional }: { label: string; dep: DepCheck | undefined; isOptional?: boolean }) {
  if (!dep) {
    return (
      <div className="flex items-center gap-3 py-2">
        <div className="w-5 h-5 rounded-full bg-muted animate-pulse" />
        <span className="text-sm">{label}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 py-2">
      {dep.ok ? (
        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
      ) : isOptional ? (
        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
      ) : (
        <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {dep.version && (
          <div className="text-xs text-muted-foreground">{dep.version}</div>
        )}
        {dep.error && (
          <div className={`text-xs ${isOptional ? "text-amber-400" : "text-red-400"}`}>{dep.error}</div>
        )}
        {isOptional && !dep.ok && (
          <div className="text-xs text-muted-foreground">Not required if Claude CLI works normally. Needed as fallback when CLI is unavailable or times out.</div>
        )}
      </div>
    </div>
  );
}

export function DepsCheckDialog({ open, onOpenChange, isBlocking = false }: DepsCheckDialogProps) {
  const depsStatus = useWorkerStore((s) => s.depsStatus);
  const checkDeps = useWorkerStore((s) => s.checkDeps);
  const restartWorker = useWorkerStore((s) => s.restart);
  const workerState = useWorkerStore((s) => s.workerState);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);

  const allOk = depsStatus?.all_ok ?? false;

  const handleCheckAgain = async () => {
    setChecking(true);
    try {
      await checkDeps();
    } catch {
      // Error is stored in depsStatus
    } finally {
      setChecking(false);
    }
  };

  const handleSaveApiKey = async () => {
    if (!apiKeyInput.trim() || !isTauri()) return;
    setSaving(true);
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      await invoke("set_secret", { key: "anthropic_api_key", value: apiKeyInput.trim() });
      setApiKeyInput("");
      if (workerState === "running") {
        await restartWorker();
      }
      await checkDeps();
    } catch (err) {
      console.error("Failed to save API key:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChange = (value: boolean) => {
    if (isBlocking && !allOk && !value) return;
    onOpenChange(value);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent hideClose={isBlocking && !allOk}>
        <DialogHeader>
          <DialogTitle>Dependencies Required</DialogTitle>
          <DialogDescription>
            The local worker needs these tools to run workflows.
          </DialogDescription>
        </DialogHeader>

        <div className="divide-y">
          {DEP_LABELS.map(({ key, label }) => (
            <DepRow
              key={key}
              label={label}
              dep={depsStatus?.[key as keyof typeof depsStatus] as DepCheck | undefined}
              isOptional={key === "api_key"}
            />
          ))}
        </div>

        {depsStatus && !depsStatus.python.ok && (
          <div className="text-xs text-muted-foreground bg-muted/40 rounded-md px-2.5 py-2">
            If Python is installed but not detected, restart Colony.
            On macOS you can also launch with <code className="font-mono">COLONY_PYTHON_BIN=/path/to/python3</code>.
          </div>
        )}

        {/* API Key input — show when api_key dep is not ok */}
        {depsStatus && !depsStatus.api_key.ok && (
          <div className="flex items-center gap-2 pt-2">
            <Key className="w-4 h-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />
            <Input
              type="password"
              placeholder="sk-ant-..."
              aria-label="Anthropic API Key"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              className="h-8 text-sm font-mono"
            />
            <Button
              size="sm"
              onClick={handleSaveApiKey}
              disabled={!apiKeyInput.trim() || saving}
              className="h-8 shrink-0"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
            </Button>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCheckAgain}
            disabled={checking}
          >
            {checking ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            )}
            Check Again
          </Button>
          <Button
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isBlocking && !allOk}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
